<script lang="ts" setup>
import OmniDialog from "@/components/omni/OmniDialog.vue";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import type { TruckDriverDto, TruckDto, TruckOwnerDto, TruckRoute } from "@/api/trucks/dto/truck.dto";
import TruckLink from "@/components/links/TruckLink.vue";
import RouteParticipant from "@/views/trips/view/components/RouteParticipant.vue";
import TruckStatusChip from "@/components/chips/TruckStatusChip.vue";
import OwnerCompanyInfo from "@/components/user/OwnerCompanyInfo.vue";
import type { TripRoute } from "@/types/trip";
import type { TripOverviewRoute, TripRouteDto } from "@/api/trips/dto/trip.dto";
import { ActiveRouteStatuses } from "@/constants/trip";
import { useTrip } from "@/composables";
import type { DriverDto } from "@/api/users/dto/driver.dto";
import type { OwnerCompany, OwnerDto } from "@/api/users/dto/owner.dto";
import { API } from "@/api/api";
import { useError } from "@/composables/useError";
import { DialogSize } from "@/constants/dialog";

const { t } = useI18n();
const { getRouteOwner, getRouteDriver, getRouteDriver2 } = useTrip();
const { showAPIError } = useError();

const api = new API();

const model = defineModel<boolean>({ default: false });

const props = defineProps<{
  title: string;
  route: TripRoute | TripRouteDto | TripOverviewRoute;
  truck: TruckDto;
}>();

const emits = defineEmits<{
  (e: "undo"): void;
}>();

const routeDriver = ref<DriverDto | TruckDriverDto | null>(null);
const routeDriver2 = ref<DriverDto | TruckDriverDto | null>(null);
const routeOwner = ref<OwnerDto | TruckOwnerDto | null>(null);
const routeOwnerCompany = ref<OwnerCompany | null>(null);
const truckOwnerCompany = ref<OwnerCompany | null>(null);
const truckActiveCurrentRoute = ref<TruckRoute | null>(null);
const loading = ref<boolean>(false);
const dialogSize = ref<DialogSize>(DialogSize.small);

const isDriverDiff = computed(() => {
  return routeDriver.value?.id !== props.truck.driverId;
});

const isDriver2Diff = computed(() => {
  if (!routeDriver2.value) {
    return false;
  }

  return routeDriver2.value?.id !== props.truck.driver2Id;
});

const isOwnerDiff = computed(() => {
  return routeOwner.value?.id !== props.truck.ownerId;
});

const isOwnerCompanyDiff = computed(() => {
  return routeDriver.value?.ownerCompanyId !== props.truck.driver?.ownerCompanyId;
});

const hasTruckActiveCurrentRoute = computed(() => {
  return truckActiveCurrentRoute.value !== null;
});

const hasDiff = computed(() => {
  const isDriverIdDiff = props.route.driverId !== props.truck.driverId;
  const isDriver2IdDiff = props.route.driver2Id !== props.truck.driver2Id;
  const isOwnerIdDiff = props.route.ownerId !== props.truck.ownerId;
  const isOwnerCompanyIdDiff = props.route.ownerCompanyId !== props.truck.driver?.ownerCompanyId;

  return isDriverIdDiff || isDriver2IdDiff || isOwnerIdDiff || isOwnerCompanyIdDiff || hasTruckActiveCurrentRoute.value;
});

const invalidReasons = computed<string[]>(() => {
  const reasons: string[] = [];

  if (hasTruckActiveCurrentRoute.value) {
    reasons.push(
      t("undo.reasons.truck-has-active-route", {
        number: props.truck.number,
      }),
    );
  }

  if (isOwnerDiff.value) {
    reasons.push(
      t("undo.reasons.owner-changed", {
        oldOwner: routeOwner.value?.name,
        newOwner: props.truck.owner.name,
      }),
    );
  }

  if (isDriverDiff.value) {
    reasons.push(
      t("undo.reasons.driver-changed", {
        oldDriver: routeDriver.value?.name,
        newDriver: props.truck.driver?.name,
      }),
    );
  }

  if (isDriver2Diff.value) {
    reasons.push(
      t("undo.reasons.driver2-changed", {
        oldDriver: routeDriver2.value?.name,
        newDriver: props.truck.driver2?.name,
      }),
    );
  }

  if (isOwnerCompanyDiff.value) {
    reasons.push(
      t("undo.reasons.owner-company-changed", {
        oldCompany: routeOwnerCompany.value?.name,
        newCompany: truckOwnerCompany.value?.name,
      }),
    );
  }

  return reasons;
});

async function loadOwnerCompany(
  ownerId: number | null | undefined,
  ownerCompanyId: number | null | undefined,
): Promise<OwnerCompany | null> {
  if (!ownerId || !ownerCompanyId) {
    return null;
  }
  try {
    const response = await api.users.findOwnerCompanyById(ownerId, ownerCompanyId);

    if (response.errorData) {
      showAPIError(response);
      return null;
    }

    return response.data || null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function loadCurrentRoute(): Promise<TruckRoute | null> {
  if (!props.truck.id) {
    return null;
  }

  try {
    const response = await api.trucks.getTruckRoutes(props.truck.id, ActiveRouteStatuses);

    if (!response.success || !response.data) {
      return null;
    }

    const activeRoute = response.data.routes.find(
      (route) => ActiveRouteStatuses.includes(route.status) && route.id !== props.route.id,
    );

    return activeRoute || null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

async function loadInfo() {
  loading.value = true;

  try {
    const [driver, driver2, owner, activeCurrentRoute] = await Promise.all([
      getRouteDriver(props.route.driverId, props.truck),
      getRouteDriver2(props.route.driver2Id, props.truck),
      getRouteOwner(props.route.ownerId, props.truck),
      loadCurrentRoute(),
    ]);

    routeDriver.value = driver;
    routeDriver2.value = driver2;
    routeOwner.value = owner;
    truckActiveCurrentRoute.value = activeCurrentRoute;

    const routeOwnerId = owner?.id;
    const truckOwnerId = props.truck.ownerId;
    const routeOwnerCompanyId = driver?.ownerCompanyId;
    const truckOwnerCompanyId = props.truck?.driver?.ownerCompanyId;

    if (routeOwnerId === truckOwnerId && routeOwnerCompanyId === truckOwnerCompanyId) {
      const company = await loadOwnerCompany(routeOwnerId, routeOwnerCompanyId);
      routeOwnerCompany.value = company;
      truckOwnerCompany.value = company;
    } else {
      routeOwnerCompany.value = await loadOwnerCompany(routeOwnerId, routeOwnerCompanyId);
      truckOwnerCompany.value = await loadOwnerCompany(truckOwnerId, truckOwnerCompanyId);
    }
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
}

function undo() {
  emits("undo");
}

watch(
  () => model.value,
  async (newValue) => {
    if (newValue) {
      await loadInfo();

      if (hasDiff.value) {
        dialogSize.value = DialogSize.large;
      }
    }
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <omni-dialog
    v-model="model"
    :disabled="hasDiff"
    :loading="loading"
    :primary-action-label="t('general.apply')"
    :primary-button-loading="loading"
    :size="dialogSize"
    :title="props.title"
    @primary-action:click="undo"
  >
    <template v-if="!loading">
      <template v-if="hasDiff">
        <span class="text-grey-darken-3">{{ t("trip.truck-details-from-route") }}:</span>

        <v-row class="mt-1">
          <v-col cols="6">
            <div class="bg-grey-lighten-4 rounded-lg pa-4">
              <truck-link
                v-if="truck.id"
                :trailer-number="truck.trailer?.number"
                :truck-id="truck.id"
                :truck-number="truck.number"
              />

              <route-participant v-if="routeDriver" :user="routeDriver" class="mt-4" />

              <route-participant v-if="routeDriver2" :user="routeDriver2" class="mt-3" is-driver2 />

              <route-participant v-if="routeOwner" :user="routeOwner" class="mt-3" />

              <div v-if="routeOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
                <owner-company-info :company="routeOwnerCompany" hide-doc-number hide-insurance-exp-exp />
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="bg-grey-lighten-4 rounded-lg pa-4">
              <div class="d-flex col-gap-3">
                <truck-link
                  v-if="truck.id"
                  :trailer-number="truck.trailer?.number"
                  :truck-id="truck.id"
                  :truck-number="truck.number"
                />

                <truck-status-chip :truck="truck" />
              </div>

              <route-participant v-if="truck.driver" :user="truck.driver" class="mt-4" />

              <route-participant v-if="truck.driver2" :user="truck.driver2" class="mt-3" is-driver2 />

              <route-participant v-if="truck.owner" :user="truck.owner" class="mt-3" />

              <div v-if="truckOwnerCompany" class="bg-white pa-4 rounded-lg mt-5">
                <owner-company-info :company="truckOwnerCompany" hide-doc-number hide-insurance-exp-exp />
              </div>
            </div>
          </v-col>
        </v-row>

        <div v-if="invalidReasons.length" class="mt-3 text-danger">
          <p>{{ t("undo.reasons.header") }}</p>
          <ul class="pl-6 mt-1">
            <li v-for="(reason, index) in invalidReasons" :key="index" class="font-weight-600">{{ reason }}</li>
          </ul>
        </div>
      </template>

      <span v-else class="text-grey-darken-1">
        {{ t("undo.confirm-undo") }}
      </span>
    </template>
  </omni-dialog>
</template>
